<template>
  <div v-if="hasMultipleRoles" class="role-switcher">
    <!-- Current Role Display / Dropdown Toggle -->
    <div
      class="item--1 sidebar-section--3 cursor-pointer"
      @click="toggleDropdown"
      :class="{ 'dropdown-open': isDropdownOpen }"
    >
      <left-icon name="user" />
      <span v-if="isOpen || isHovered" class="icon--label"> Role </span>
      <unicon
        v-if="isOpen || isHovered"
        name="angle-down"
        fill="#f2f2f0"
        class="icon--2 w-6 h-6"
        :class="[isDropdownOpen ? 'svg-flipped' : 'svg']"
      />
    </div>

    <!-- Role Dropdown -->
    <transition-expand>
      <div v-if="isDropdownOpen && (isOpen || isHovered)">
        <div class="h-px bg-gray-700 mx-5 my-2"></div>
        <div
          v-for="role in availableRoles"
          :key="role"
          class="item--1 sidebar-section--3 sublink cursor-pointer"
          :class="{ 'active-role': role === activeRole }"
          @click="selectRole(role)"
        >
          <span class="icon--1">
            <!-- Empty icon space to align with other submenu items -->
          </span>
          <span class="icon--label font-bold">
            {{ formatRoleName(role) }}
          </span>
        </div>
        <div class="h-px bg-gray-700 mx-5 my-2"></div>
      </div>
    </transition-expand>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import LeftIcon from '@/components/SideNav--Item-Icon'
import TransitionExpand from '@/components/TransitionExpand'

export default {
  name: 'RoleSwitcher',
  components: {
    LeftIcon,
    TransitionExpand
  },
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    isHovered: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isDropdownOpen: false,
      isLoading: false
    }
  },
  computed: {
    ...mapGetters('user', ['activeRole', 'availableRoles', 'hasMultipleRoles'])
  },
  methods: {
    ...mapActions('user', ['switchRole']),

    toggleDropdown() {
      if (!this.hasMultipleRoles) return
      this.isDropdownOpen = !this.isDropdownOpen
    },

    async selectRole(role) {
      if (role === this.activeRole || this.isLoading) return

      this.isLoading = true
      this.isDropdownOpen = false

      try {
        await this.switchRole(role)

        // Emit event to parent components that role has changed
        this.$emit('role-changed', role)

        // Only refresh if we're on a page that might have role-based restrictions
        const restrictedRoutes = ['/reports', '/task-audit', '/settings']
        const currentPath = this.$route.path
        const needsRefresh = restrictedRoutes.some((route) =>
          currentPath.includes(route)
        )

        if (needsRefresh) {
          this.$nextTick(() => {
            this.$router.go(0)
          })
        }
      } catch (error) {
        console.error('Failed to switch role:', error)
      } finally {
        this.isLoading = false
      }
    },

    formatRoleName(role) {
      const roleNames = {
        staff: 'Staff',
        manager: 'Manager',
        admin: 'Admin',
        developer: 'Developer'
      }
      return roleNames[role] || role
    },

    getRoleIcon(role) {
      const roleIcons = {
        staff: 'user',
        manager: 'user-check',
        admin: 'cog',
        developer: 'code'
      }
      return roleIcons[role] || 'user'
    },

    // Close dropdown when clicking outside
    handleClickOutside(event) {
      if (!this.$el.contains(event.target)) {
        this.isDropdownOpen = false
      }
    }
  },

  mounted() {
    document.addEventListener('click', this.handleClickOutside)
  },

  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside)
  }
}
</script>

<style lang="scss" scoped>
.role-switcher {
  /* Use the same hover behavior as other sidebar items */
  .item--1:not(.router-link-exact-active):not(.no-hover):hover {
    background-color: rgb(
      31,
      41,
      55
    ); /* equivalent to Tailwind's bg-gray-800 */
  }

  /* Style for dropdown open state */
  .dropdown-open {
    background-color: rgb(31, 41, 55); /* bg-gray-800 */
  }

  /* Submenu styling to match Tasks submenu exactly */
  .sublink {
    padding-left: 24px;
    opacity: 0.85;
    transition: opacity 0.2s ease;
  }

  .sublink:hover {
    opacity: 1;
  }

  .sublink.active-role {
    opacity: 1;
  }

  /* Style for active role option - matches Tasks submenu pattern */
  .active-role {
    background-color: rgba(59, 130, 246, 0.15); /* Light blue background */
    border-left: 3px solid #3b82f6; /* Blue border */
    padding-left: 21px; /* Adjust for the border */
  }
}

/* Animation performance improvements - match Tasks submenu timing exactly */
.expand-enter,
.expand-leave-to {
  height: 0;
  opacity: 0;
}

.expand-enter-active {
  overflow: hidden;
  transition: height 0.25s 0.1s ease-out, opacity 0.2s 0.2s ease-out;
}

.expand-leave-active {
  overflow: hidden;
  transition: opacity 0.05s ease-out, height 0.2s 0.1s ease-in;
}

/* Hardware acceleration for better performance - match Tasks submenu */
.expand-enter-active,
.expand-leave-active,
.svg-flipped,
.svg {
  will-change: transform, height, opacity;
  transform: translateZ(0);
}

/* Arrow rotation animations - match Tasks submenu exactly */
.svg-flipped {
  transition: transform 0.25s 0.3s ease-out;
  transform-origin: 50% 50%;
  transform: rotate(-180deg);
}

.svg {
  transition: transform 0.3s 0.03s ease-out;
  transform-origin: 50% 50%;
  transform: rotate(0);
}
</style>
